import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  VehicleRental,
   VEHICLE_RENTAL_BILLING_LABELS,
  VEHICLE_RENTAL_STATUS_LABELS,
} from '../../types';
import vehicleRentalService from '../../services/vehicleRentalService';
import { useAuthUser } from '../../contexts/AuthUserContext';

const formatCurrency = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n))
    ? Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (d?: string | null) => {
  if (!d) return '—';
  try {
    return format(parseISO(d.length > 10 ? d : `${d}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return d;
  }
};

const RentalList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { canWrite } = useAuthUser();
  const [items, setItems] = useState<VehicleRental[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await vehicleRentalService.list();
        if (!cancelled) setItems(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Não foi possível carregar as locações. Verifique se a API está disponível.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = items.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const vehicleLabel = r.vehicle
      ? `${r.vehicle.brand} ${r.vehicle.model} ${r.vehicle.year}`.toLowerCase()
      : '';
    return (
      r.renter_name.toLowerCase().includes(q) ||
      r.renter_phone.toLowerCase().includes(q) ||
      r.renter_document.toLowerCase().includes(q) ||
      vehicleLabel.includes(q) ||
      String(r.id).includes(q)
    );
  });

  const statusColor = (s: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (s) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <CarIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Locações
          </Typography>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/locacoes/novo')} sx={{ borderRadius: 2 }}>
              Nova locação
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por locatário, veículo ou ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                <TableCell>ID</TableCell>
                <TableCell>Veículo</TableCell>
                <TableCell>Locatário</TableCell>
                <TableCell>Cobrança</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {items.length === 0 ? 'Nenhuma locação cadastrada.' : 'Nenhum resultado para a busca.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const v = r.vehicle;
                  const vehicleLabel = v ? `${v.brand} ${v.model} (${v.year})` : `Veículo #${r.vehicle_id}`;
                  const amount =
                    r.billing_type === 'daily'
                      ? r.total_amount ??
                        (r.daily_rate != null && r.number_of_days != null
                          ? Number(r.daily_rate) * Number(r.number_of_days)
                          : null)
                      : r.monthly_amount;
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{vehicleLabel}</TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>{r.renter_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {r.renter_phone}
                        </Typography>
                      </TableCell>
                      <TableCell>{VEHICLE_RENTAL_BILLING_LABELS[r.billing_type]}</TableCell>
                      <TableCell>
                        {formatDate(r.start_date)}
                        {r.end_date ? ` — ${formatDate(r.end_date)}` : ''}
                      </TableCell>
                      <TableCell>{formatCurrency(amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={VEHICLE_RENTAL_STATUS_LABELS[r.status]} color={statusColor(r.status)} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<ViewIcon />} onClick={() => navigate(`/locacoes/${r.id}`)}>
                          Ver
                        </Button>
                        {canWrite && (
                          <Button size="small" startIcon={<EditIcon />} onClick={() => navigate(`/locacoes/editar/${r.id}`)}>
                            Editar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    </Fade>
  );
};

export default RentalList;
